import { Input, TextArea } from "@jhool-io/fe-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import DiagnosisCodeSelect from "../../../notes/components/DiagnosisCodeSelect/DiagnosisCodeSelect";
import { ISignLabRequestForm } from "../../types/e-prescribe.types";

interface SignLabRequestFormProps {
    onSubmit(values: ISignLabRequestForm): void;
}

export const SIGN_LAB_REQUEST_FORM_ID = "sign-lab-request";

const signLabRequestSchema: z.ZodType<ISignLabRequestForm> = z.object({
    diagnosis_codes: z
        .array(z.string())
        .max(3, "You can only select up to 3 diagnosis codes"),
    comment: z.string(),
    signature: z
        .string()
        .trim()
        .min(1, "Signature is required")
        .max(255, "Signature must be 255 characters or fewer"),
});

export default function SignLabRequestForm({
    onSubmit,
}: SignLabRequestFormProps) {
    const {
        control,
        formState: { errors },
        handleSubmit,
        register,
    } = useForm<ISignLabRequestForm>({
        resolver: zodResolver(signLabRequestSchema),
        defaultValues: {
            diagnosis_codes: [],
            comment: "",
            signature: "",
        },
    });

    return (
        <form
            id={SIGN_LAB_REQUEST_FORM_ID}
            onSubmit={handleSubmit(onSubmit)}
            autoComplete="off"
        >
            <div className="flex flex-col gap-[--spacing-items]">
                <Controller
                    name="diagnosis_codes"
                    control={control}
                    render={({ field }) => (
                        <DiagnosisCodeSelect
                            label="Diagnosis Codes"
                            value={field.value}
                            onChange={(value) =>
                                field.onChange(
                                    Array.isArray(value) ? value : []
                                )
                            }
                            placeholder="Select diagnosis codes"
                            errorText={errors.diagnosis_codes?.message}
                            isMulti
                            isOptional
                            isClearable
                            closeMenuOnSelect={false}
                            isLongListInDialog
                        />
                    )}
                />
                <TextArea
                    {...register("comment")}
                    label="Comment"
                    placeholder="Add a comment"
                    isOptional
                />
                <Input
                    {...register("signature")}
                    label="Signature"
                    placeholder="Provider's initials"
                    isRequired
                    hasError={Boolean(errors.signature)}
                    errorText={errors.signature?.message}
                />
            </div>
        </form>
    );
}
